// Api
import DeliveryApi from '@/api/DeliveryApi';

// Utils
import DataUtil from '@/utils/DataUtil';
import ValidationUtil from '@/utils/ValidationUtil';
import modules from './modules';

const actionTypes = {
    inTransit: {
        getInTransitDeliveries: 'getInTransitDeliveries',
    },
    reset: 'reset',
};
const mutationTypes = {
    interface: {
        SET_INTERFACE_MODE: 'SET_INTERFACE_MODE',
    },
    inTransit: {
        SET_IN_TRANSIT_LIST: 'SET_IN_TRANSIT_LIST',
        SET_IN_TRANSIT_IN_FLIGHT: 'SET_IN_TRANSIT_IN_FLIGHT',
        SET_IN_TRANSIT_LAST_CHECK: 'SET_IN_TRANSIT_LAST_CHECK',
    },
};

// Enums for the interface mode
const interfaceModes = {
    SIMPLIFIED: 'SIMPLIFIED',
    DETAILED: 'DETAILED',
};

export default {
    namespaced: true,
    modules,
    state: {
        interface: {
            /** @type { String } */
            mode: interfaceModes.SIMPLIFIED,
        },

        inTransit: {
            /** @type { Object } */
            list: {},
            /** @type { Boolean } */
            inFlight: false,
            /** @type { Number } */
            lastCheck: 0,
        },
    },
    getters: {
        /** @returns { Boolean } */
        interfaceIsSimplified: ( state ) => state.interface.mode === interfaceModes.SIMPLIFIED,
        /** @returns { Boolean } */
        interfaceIsDetailed: ( state ) => state.interface.mode === interfaceModes.DETAILED,

        /** @returns { Array } */
        inTransitList: ( state ) => Object.values( state.inTransit.list ),
        /** @returns { Number } */
        inTransitListCount: ( state, getters ) => getters.inTransitList.length,
        /** @returns { Boolean } */
        hasInTransitList: ( state, getters ) => ( getters.inTransitListCount > 0 ),
    },
    actions: {
        [ actionTypes.reset ]( { commit } ) {
            commit( mutationTypes.interface.SET_INTERFACE_MODE, { mode: interfaceModes.SIMPLIFIED } );
            commit( mutationTypes.inTransit.SET_IN_TRANSIT_IN_FLIGHT, { boolean: false } );
            commit( mutationTypes.inTransit.SET_IN_TRANSIT_LIST, { keyedList: {} } );
            commit( mutationTypes.inTransit.SET_IN_TRANSIT_LAST_CHECK, { dateTimeStamp: 0 } );
        },

        async [ actionTypes.inTransit.getInTransitDeliveries ]( context ) {
            context.commit( mutationTypes.inTransit.SET_IN_TRANSIT_IN_FLIGHT, { boolean: true } );
            try {
                const { data } = await DeliveryApi.getInTransitDeliveries( {
                    jwtToken: context.rootState.User.jwtToken,
                    websiteId: context.rootGetters[ 'Website/id' ],
                    userAccountId: context.rootState.User.accountId,
                } );
                context.commit( mutationTypes.inTransit.SET_IN_TRANSIT_LAST_CHECK, {
                    dateTimeStamp: ( new Date() ).getTime(),
                } );
                // Set our list of inTransit items
                context.commit( mutationTypes.inTransit.SET_IN_TRANSIT_LIST, {
                    keyedList: ValidationUtil.arrayHasItems( data.mailSearchListDTO )
                        ? DataUtil.normalize( data.mailSearchListDTO, 'id' )
                        : {},
                } );
                // Populate our picklist with inTransit items
                await context.dispatch( 'Mail/Delivery/Current/transformAndSetPicklistItems', {
                    list: ValidationUtil.arrayHasItems( data.mailSearchListDTO )
                        ? data.mailSearchListDTO
                        : [],
                }, { root: true } );
            } catch ( error ) {
                console.error( 'Error: Failed to get in-transit deliveries: ', error );
            } finally {
                context.commit( mutationTypes.inTransit.SET_IN_TRANSIT_IN_FLIGHT, { boolean: false } );
            }
        },
    },
    mutations: {
        [ mutationTypes.interface.SET_INTERFACE_MODE ]( state, { mode } ) {
            state.interface.mode = Object.values( interfaceModes ).includes( mode )
                ? mode
                : interfaceModes.SIMPLIFIED;
        },

        [ mutationTypes.inTransit.SET_IN_TRANSIT_LIST ]( state, { keyedList } ) {
            state.inTransit.list = keyedList;
        },
        [ mutationTypes.inTransit.SET_IN_TRANSIT_IN_FLIGHT ]( state, { boolean } ) {
            state.inTransit.inFlight = boolean;
        },
        [ mutationTypes.inTransit.SET_IN_TRANSIT_LAST_CHECK ]( state, { dateTimeStamp } ) {
            state.inTransit.lastCheck = dateTimeStamp;
        },
    },
};
